import {
  INVITATION_MAPPING_RESOLUTION_REASON,
  INVITATION_MAPPING_STATUS,
  isInvitationTerminal,
  normalizeInvitationEmail,
} from "./invitationIdentity";

const normalizeString = (value) =>
  typeof value === "string" ? value.trim() : "";

const normalizeClientEmail = (client) =>
  normalizeInvitationEmail(client?.invitationEmail ?? client?.email ?? "");

const normalizeInvitationId = (client) =>
  normalizeString(client?.invitationId).toLowerCase();

const resolveInvitationCreatedAtMs = (client) => {
  const value = Number(client?.invitationCreatedAtMs);
  if (Number.isFinite(value) && value >= 0) {
    return value;
  }

  return Number.NEGATIVE_INFINITY;
};

const normalizeSummaryInvitationId = (summary) =>
  normalizeString(summary?.invitationId).toLowerCase();

const normalizeSummaryInvitationEmail = (summary) =>
  normalizeInvitationEmail(summary?.invitationEmail ?? "");

const resolveSummaryTimestampMs = (summary) => {
  const value = Number(summary?.timestampMs);
  if (Number.isFinite(value) && value >= 0) {
    return value;
  }

  return Number.NEGATIVE_INFINITY;
};

const dedupeActiveUserIds = (activeUserIds = []) => {
  if (!Array.isArray(activeUserIds)) return [];

  const seen = new Set();
  const deduped = [];

  activeUserIds.forEach((rawUserId) => {
    const userId = normalizeString(rawUserId);
    if (!userId) return;

    const normalized = userId.toLowerCase();
    if (seen.has(normalized)) return;
    seen.add(normalized);
    deduped.push(userId);
  });

  return deduped;
};

export const normalizeMappingEmail = (value) => normalizeInvitationEmail(value);

export const selectCanonicalPendingClientForEmail = (
  clients,
  normalizedEmail
) => {
  if (!Array.isArray(clients) || !normalizedEmail) {
    return null;
  }

  const pendingCandidates = clients
    .filter((client) => client?.status !== "active-string")
    .filter((client) => normalizeInvitationId(client))
    .filter((client) => normalizeClientEmail(client) === normalizedEmail);

  if (pendingCandidates.length === 0) {
    return null;
  }

  pendingCandidates.sort((a, b) => {
    const timeA = resolveInvitationCreatedAtMs(a);
    const timeB = resolveInvitationCreatedAtMs(b);
    if (timeA !== timeB) {
      return timeB - timeA;
    }

    const invitationIdA = normalizeInvitationId(a);
    const invitationIdB = normalizeInvitationId(b);
    return invitationIdA.localeCompare(invitationIdB);
  });

  return pendingCandidates[0] ?? null;
};

export const shouldSkipPendingClientCreation = ({
  normalizedInvitationEmail,
  activeClients,
}) => {
  if (!normalizedInvitationEmail || !Array.isArray(activeClients)) {
    return false;
  }

  return activeClients.some(
    (activeClient) =>
      normalizeClientEmail(activeClient) === normalizedInvitationEmail
  );
};

export const selectCanonicalInvitationSummaryForEmail = (
  invitationSummaries,
  normalizedEmail
) => {
  if (!Array.isArray(invitationSummaries) || !normalizedEmail) {
    return null;
  }

  const candidates = invitationSummaries
    .filter((summary) => normalizeSummaryInvitationId(summary))
    .filter((summary) => !isInvitationTerminal(summary?.state))
    .filter(
      (summary) => normalizeSummaryInvitationEmail(summary) === normalizedEmail
    );

  if (candidates.length === 0) {
    return null;
  }

  candidates.sort((left, right) => {
    const leftTimestamp = resolveSummaryTimestampMs(left);
    const rightTimestamp = resolveSummaryTimestampMs(right);
    if (leftTimestamp !== rightTimestamp) {
      return rightTimestamp - leftTimestamp;
    }

    const leftInvitationId = normalizeSummaryInvitationId(left);
    const rightInvitationId = normalizeSummaryInvitationId(right);
    return leftInvitationId.localeCompare(rightInvitationId);
  });

  return candidates[0] ?? null;
};

export const buildInvitationMappingAuditPlan = ({
  invitationSummaries,
  normalizedEmail,
  activeUserIds,
}) => {
  if (!Array.isArray(invitationSummaries) || !normalizedEmail) {
    return [];
  }

  const matchingSummaries = invitationSummaries.filter(
    (summary) =>
      normalizeSummaryInvitationEmail(summary) === normalizedEmail &&
      !isInvitationTerminal(summary?.state)
  );
  if (matchingSummaries.length === 0) {
    return [];
  }

  const dedupedActiveUserIds = dedupeActiveUserIds(activeUserIds);
  if (dedupedActiveUserIds.length === 0) {
    return [];
  }

  if (dedupedActiveUserIds.length > 1) {
    return matchingSummaries
      .filter((summary) => normalizeSummaryInvitationId(summary))
      .map((summary) => ({
        invitationId: normalizeString(summary.invitationId),
        patch: {
          mapping_status: INVITATION_MAPPING_STATUS.CONFLICT,
          mapped_user_id: "",
          superseded_by_invitation_id: "",
          mapping_resolution_reason:
            INVITATION_MAPPING_RESOLUTION_REASON.UNRESOLVED_MULTIPLE_ACTIVE_USERS,
        },
      }));
  }

  const canonicalInvitation = selectCanonicalInvitationSummaryForEmail(
    matchingSummaries,
    normalizedEmail
  );
  if (!canonicalInvitation?.invitationId) {
    return [];
  }

  const canonicalInvitationId = normalizeString(canonicalInvitation.invitationId);
  const mappedUserId = dedupedActiveUserIds[0];

  return matchingSummaries
    .filter((summary) => normalizeSummaryInvitationId(summary))
    .map((summary) => {
      const invitationId = normalizeString(summary.invitationId);
      const isCanonical = invitationId === canonicalInvitationId;

      return {
        invitationId,
        patch: {
          mapping_status: isCanonical
            ? INVITATION_MAPPING_STATUS.ACTIVE
            : INVITATION_MAPPING_STATUS.SUPERSEDED,
          mapped_user_id: mappedUserId,
          superseded_by_invitation_id: isCanonical ? "" : canonicalInvitationId,
          mapping_resolution_reason: isCanonical
            ? INVITATION_MAPPING_RESOLUTION_REASON.CANONICAL_ACTIVE_USER
            : INVITATION_MAPPING_RESOLUTION_REASON.SUPERSEDED_BY_CANONICAL_INVITATION,
        },
      };
    });
};
